<template>
  <div class="video-tab">
    <section class="video-tab__section">
      <h3 class="video-tab__heading">Manifest</h3>
      <div v-if="store.manifestUrl.value" class="video-tab__block">
        <div class="video-tab__line">
          <span class="video-tab__label">URL</span>
          <span class="video-tab__value video-tab__value--url">{{
            trimManifestUrlForDisplay(store.manifestUrl.value)
          }}</span>
        </div>
        <div class="video-tab__line">
          <span class="video-tab__label">Protocol</span>
          <span class="video-tab__value">{{ store.manifestProtocol.value || '—' }}</span>
        </div>
        <div v-if="store.manifestLabels.value.length" class="video-tab__line">
          <span class="video-tab__label">Offers</span>
          <span class="video-tab__value">
            <span v-for="label in store.manifestLabels.value" :key="label" class="video-tab__pill">{{ label }}</span>
          </span>
        </div>
        <div v-if="store.availableRenditions.value.length" class="video-tab__line video-tab__line--renditions">
          <span class="video-tab__label">Renditions</span>
          <span class="video-tab__value">
            <span
              v-for="(r, i) in sortedRenditions"
              :key="r.id || i"
              class="video-tab__pill video-tab__pill--rendition"
              >{{ renditionLabel(r) }}</span
            >
          </span>
        </div>
        <div v-if="store.availableAudioTracks.value.length" class="video-tab__line video-tab__line--audios">
          <span class="video-tab__label">Audios</span>
          <span class="video-tab__value">
            <span
              v-for="(a, i) in store.availableAudioTracks.value"
              :key="a.id || i"
              class="video-tab__pill video-tab__pill--audio"
              >{{ audioTrackLabel(a) }}</span
            >
          </span>
        </div>
      </div>
      <div v-else class="video-tab__muted">No manifest loaded.</div>
    </section>

    <section class="video-tab__section">
      <h3 class="video-tab__heading">Playback</h3>
      <div v-if="store.playbackInfo.value" class="video-tab__block">
        <div class="video-tab__line">
          <span class="video-tab__label">Bitrate</span>
          <span class="video-tab__value">{{ formatBitrate(store.playbackInfo.value.bitrate) }}</span>
        </div>
        <div class="video-tab__line">
          <span class="video-tab__label">Resolution</span>
          <span class="video-tab__value">{{ resolutionText(store.playbackInfo.value) }}</span>
        </div>
        <div class="video-tab__line">
          <span class="video-tab__label">Video codec</span>
          <span class="video-tab__value">{{ store.playbackInfo.value.videoCodec || '—' }}</span>
        </div>
        <div class="video-tab__line">
          <span class="video-tab__label">Audio codec</span>
          <span class="video-tab__value">{{ store.playbackInfo.value.audioCodec || '—' }}</span>
        </div>
        <div class="video-tab__line">
          <span class="video-tab__label">Quality</span>
          <span class="video-tab__value">{{ store.playbackInfo.value.qualityLabel || '—' }}</span>
        </div>
        <div class="video-tab__line">
          <span class="video-tab__label">Playing format</span>
          <span class="video-tab__value">
            <template v-if="store.playbackInfo.value.dolbyVision">Dolby Vision</template>
            <template v-else-if="store.playbackInfo.value.hdrType">{{ store.playbackInfo.value.hdrType }}</template>
            <template v-else>SDR</template>
          </span>
        </div>
      </div>
      <div v-else class="video-tab__muted">No playback info.</div>
    </section>

    <section class="video-tab__section">
      <h3 class="video-tab__heading">Device capabilities</h3>
      <div v-if="store.deviceCapabilities.value" class="video-tab__block">
        <div class="video-tab__line">
          <span class="video-tab__label">4K / UHD panel</span>
          <span class="video-tab__value">{{ store.deviceCapabilities.value.uhd ? 'Yes' : 'No' }}</span>
        </div>
        <div class="video-tab__line">
          <span class="video-tab__label">HDR</span>
          <span class="video-tab__value">{{ store.deviceCapabilities.value.hdr ? 'Yes' : 'No' }}</span>
        </div>
        <div class="video-tab__line">
          <span class="video-tab__label">Dolby Vision</span>
          <span class="video-tab__value">{{ store.deviceCapabilities.value.dolbyVision ? 'Yes' : 'No' }}</span>
        </div>
      </div>
      <div v-else class="video-tab__muted">Loading device capabilities…</div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue';
import { useVideoDebugStore } from '@/store/video-debug';
import type { ManifestRendition, ManifestAudioTrack } from '@/store/video-debug';
import { getDeviceCapabilitiesForDebug } from '@/utils/device';

const store = useVideoDebugStore();

const sortedRenditions = computed(() => {
  const list = [...store.availableRenditions.value];
  list.sort((a, b) => (b.height ?? 0) - (a.height ?? 0));
  return list;
});

function formatBitrate(bps: number | undefined): string {
  if (bps == null) return '—';
  if (bps >= 1e6) return `${(bps / 1e6).toFixed(2)} Mbps`;
  if (bps >= 1e3) return `${(bps / 1e3).toFixed(1)} Kbps`;
  return `${bps} bps`;
}

function renditionLabel(r: ManifestRendition): string {
  const h = r.height;
  const br = r.bitrate;
  if (h != null && br != null) return `${h}p · ${formatBitrate(br)}`;
  if (h != null) return `${h}p`;
  if (br != null) return formatBitrate(br);
  return '—';
}

function audioTrackLabel(a: ManifestAudioTrack): string {
  if (a.label) return a.label;
  if (a.lang) return a.lang;
  return a.id ?? '—';
}

/** Replace JWT/token path segment and query string so URL is safe to display. */
function trimManifestUrlForDisplay(url: string): string {
  if (!url || typeof url !== 'string') return url;
  const match = url.match(/^(https?:\/\/[^/]+)\/[^/]+(\/.*?)(?:\?.*)?$/);
  if (match) return match[1] + '/...' + match[2];
  return url;
}

function resolutionText(info: { width?: number; height?: number }): string {
  const w = info.width;
  const h = info.height;
  if (w != null && h != null) return `${w}×${h}`;
  return '—';
}

onMounted(async () => {
  const caps = await getDeviceCapabilitiesForDebug();
  store.setDeviceCapabilities(caps);
});
</script>

<style lang="less" scoped>
.video-tab {
  padding: 8px 0;
}

.video-tab__section {
  margin-bottom: 16px;
}

.video-tab__heading {
  margin: 0 0 6px;
  color: #00c8b4;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.video-tab__block {
  background: rgba(0, 0, 0, 0.35);
  border-radius: 4px;
  padding: 8px 10px;
}

.video-tab__line {
  display: flex;
  gap: 10px;
  margin: 5px 0;
  font-size: 12px;
}

.video-tab__line--summary {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 200, 180, 0.2);
}

.video-tab__label {
  flex-shrink: 0;
  min-width: 120px;
  color: #888;
}

.video-tab__value {
  color: #e0e0e0;
  word-break: break-all;
}

.video-tab__value--url {
  font-size: 11px;
  color: #aaa;
}

.video-tab__pill {
  display: inline-block;
  margin-right: 6px;
  margin-bottom: 4px;
  padding: 2px 8px;
  background: rgba(0, 200, 180, 0.25);
  border-radius: 4px;
  color: #00c8b4;
  font-size: 11px;
}

.video-tab__pill--rendition {
  background: rgba(136, 170, 255, 0.2);
  color: #88aaff;
}

.video-tab__line--renditions .video-tab__value {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.video-tab__line--audios .video-tab__value {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.video-tab__pill--audio {
  background: rgba(0, 200, 120, 0.2);
  color: #00c878;
}

.video-tab__muted {
  color: #666;
  font-style: italic;
  font-size: 12px;
}
</style>
